import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { User, SubscriptionStatus } from '../types';
import { authService } from '../services/api';

interface AuthContextType {
  user: User | null;
  subscription: SubscriptionStatus | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<User>;
  register: (username: string, email: string, password: string, referralCode?: string) => Promise<User>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  checkSubscription: () => Promise<SubscriptionStatus | null>;
  updateCoins: (coins: number) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [subscription, setSubscription] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);

  const checkSubscription = useCallback(async (): Promise<SubscriptionStatus | null> => {
    try {
      const subStatus = await authService.getSubscriptionStatus();
      setSubscription(subStatus);
      return subStatus;
    } catch (error) {
      console.error('Failed to check subscription:', error);
      return null;
    }
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
      
      if (currentUser) {
        await checkSubscription();
      } else {
        setSubscription(null);
      }
    } catch (error) {
      console.error('Failed to refresh user:', error);
      setUser(null);
      setSubscription(null);
    }
  }, [checkSubscription]);

  useEffect(() => {
    const initAuth = async () => {
      setLoading(true);
      await refreshUser();
      setLoading(false);
    };
    
    initAuth();
  }, [refreshUser]);

  const login = async (email: string, password: string): Promise<User> => {
    const loggedInUser = await authService.login(email, password);
    setUser(loggedInUser);
    
    await checkSubscription();
    
    return loggedInUser;
  };

  const register = async (username: string, email: string, password: string, referralCode?: string): Promise<User> => {
    const registeredUser = await authService.register(username, email, password, referralCode);
    setUser(registeredUser);
    
    await checkSubscription();
    
    return registeredUser;
  };

  const logout = async (): Promise<void> => {
    await authService.logout();
    setUser(null);
    setSubscription(null);
  };

  const updateCoins = (coins: number) => {
    if (user) {
      const updatedUser = { ...user, coins };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    }
  };

  const value: AuthContextType = {
    user,
    subscription,
    loading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    refreshUser,
    checkSubscription,
    updateCoins,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;
